import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DashboardLayout } from "../../../components/layout";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightFormRequestDtActions from "../../../components/admin/FreightFormRequestDtActions";
import FreightFormRequestStatusColumn from "../../../components/admin/FreightFormRequestStatusColumn";
import { AdminServerList, DataTablePageSize } from "../../../components/datatable";
import { FREIGHT_FORM_REQUEST_COLUMNS } from "../../../config/freightAdminColumns";
import { buildFreightFormRequestDataTablesBody } from "../../../config/freightFormRequestDataTable";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import { postFreightFormRequestData } from "../../../utils/api";
import { parseFreightUserDataTable } from "../../../utils/freightUserDataTable";
import { FREIGHT_DT_LENGTH_MENU_25 } from "../../../components/admin/freightServerSideDtDefaults";
import "./adminFreight.css";

const TABLE_ID = "freight_form_requests";
const COLUMN_DEFS = FREIGHT_FORM_REQUEST_COLUMNS;

const FreightFormRequestsPage = () => {
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Freight form requests" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const lengthMenu = useMemo(() => Array.from(FREIGHT_DT_LENGTH_MENU_25[0]), []);
  const [pageSize, setPageSize] = useState(() => FREIGHT_DT_LENGTH_MENU_25[0][0]);

  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const tableRef = useRef(null);
  const drawRef = useRef(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const handleCounts = useCallback((filtered, total) => {
    setRecordsFiltered(typeof filtered === "number" && filtered >= 0 ? filtered : 0);
    setRecordsTotal(typeof total === "number" && total >= 0 ? total : 0);
  }, []);

  const fetchPage = useCallback(async ({ page, pageSize: ps, search: q, sortBy, sortDir }) => {
    const draw = drawRef.current++;
    const start = (page - 1) * ps;
    const body = buildFreightFormRequestDataTablesBody({
      draw,
      start,
      length: ps,
      searchValue: q,
      sortBy,
      sortDir
    });
    const res = await postFreightFormRequestData(body);
    const parsed = parseFreightUserDataTable(res);
    const list = (parsed.rows || []).map((r) => ({
      ...r,
      _rowKey: `${String(r.type || "").toLowerCase()}-${r.id}`
    }));
    return {
      list,
      recordsTotal: parsed.recordsTotal,
      recordsFiltered: parsed.recordsFiltered
    };
  }, []);

  const reloadTable = useCallback(() => tableRef.current?.reload?.(), []);

  const renderActions = useCallback(
    (row) => <FreightFormRequestDtActions row={row} onUpdated={reloadTable} />,
    [reloadTable]
  );

  const prependColumns = useMemo(
    () => [
      {
        id: "form_request_status",
        header: "Status",
        headerTitle: "Pending, complete, or rejected",
        thClassName: "admin-dt-th--narrow",
        tdClassName: "align-middle",
        renderCell: (row) => <FreightFormRequestStatusColumn row={row} onUpdated={reloadTable} />
      }
    ],
    [reloadTable]
  );

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search (RM, user, locations, material, status, remarks)…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">
              Freight form requests ({recordsFiltered}
              {recordsTotal !== recordsFiltered ? ` of ${recordsTotal}` : ""})
            </h3>
            <DataTablePageSize value={pageSize} onChange={setPageSize} options={lengthMenu} />
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Buyer and seller freight enquiry forms. Use <strong>Actions</strong> for remarks and the{" "}
            <strong>Status</strong> column for pending, complete, or rejected. Sort and search are processed on the
            server.
          </p>

          <AdminServerList
            ref={tableRef}
            fetchPage={fetchPage}
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            prependColumns={prependColumns}
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
            search={debouncedSearch}
            showSearchInput={false}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            lengthMenu={lengthMenu}
            defaultSortKey="created_at"
            defaultSortDir="desc"
            onCountsChange={handleCounts}
            emptyText="No form requests found."
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightFormRequestsPage;
